import { gql } from "@apollo/client";

const MOMENT_CLIP_FIELDS = gql`
  fragment MomentClipFields on Clips {
    _id
    clipImageThumb
    clipLink
    clipTitle
    shareId
    status
    userClipTitle
    username
  }
`;

const MATCH_VOD_FIELDS = gql`
  fragment MatchVodFields on MatchVod {
    id
    status
    thumbnail
    url
    shareId
    roundMarkers {
      round
      momentId
      startMs
      endMs
    }
  }
`;

// Quentin exposes playerStat specifically for match-history rows, where the
// full scoreboard is not queried. Costs 5 against the complexity ceiling.
const MATCH_RANK_FIELDS = gql`
  fragment MatchRankFields on Match {
    playerStat {
      rank
      rankType
      rankOld
      rankNew
    }
  }
`;

export const EXTERNAL_MATCH_REFRESH = gql`
  mutation Mutation($game: Game!) {
    externalMatchesRefresh(game: $game) {
      success
      totalMatches
      errors {
        message
        field
        code
      }
    }
  }
`;

export const ROLL_SHARE_CODES = gql`
  query RollShareCodes($simulate: ESimulateOptions) {
    rollShareCodes(simulate: $simulate) {
      status
    }
  }
`;

export const REFRESH_FACEIT = gql`
  mutation RefreshFaceit {
    getFaceitHistory
  }
`;

export const GET_MATCH = gql`
  query GetMatchQuery($csgoMatchId: String!) {
    requestParsing(csgoMatchId: $csgoMatchId)
  }
`;

export const CHECK_FOR_CLIP = gql`
  query CheckClipCreated(
    $steamId: String
    $round: Int
    $replayLinkStore: String
  ) {
    checkClipCreated(
      steamId: $steamId
      round: $round
      replayLinkStore: $replayLinkStore
    ) {
      inTransit
      hasClip
      clip {
        status
        username
        clipLink
        clipImageThumb
        clipTitle
        userClipTitle
        shareId
      }
    }
  }
`;

export const STEAM_FRIENDS = gql`
  query SteamFriends {
    steamFriends {
      avatarUrl
      displayName
      steamId64
      user
    }
  }
`;

export const MATCHES = gql`
  query Matches(
    $playerGameId: String!
    $after: String
    $first: Int
    $game: Game
  ) {
    matches(
      playerGameId: $playerGameId
      after: $after
      first: $first
      game: $game
    ) {
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
        totalCount
      }
      nodes {
        id
        title
        thumbnail
        score
        role
        result
        demoUrl
        playerCount
        placement
        momentCount
        assistCount
        deathCount
        status
        durationInSeconds
        game
        outdated
        gameMode
        killCount
        matchPlayedAt
        knockdowns
        hero
        matchVod {
          ...MatchVodFields
        }
        ...MatchRankFields
        players {
          accountName
          avatar
          displayName
          gameId
          team
        }
        player {
          avatar
          accountName
          displayName
          gameId
          team
        }
      }
      edges {
        cursor
      }
    }
  }
  ${MATCH_VOD_FIELDS}
  ${MATCH_RANK_FIELDS}
`;

export const MATCHES_WITH_FILTERS = gql`
  query MatchesWithFilters(
    $playerGameId: String!
    $after: String
    $first: Int
    $game: Game
    $map: String
    $platform: String
    $wingman: Boolean
    $result: String
    $minKills: Int
    $minMomentKills: Int
  ) {
    matches(
      playerGameId: $playerGameId
      after: $after
      first: $first
      game: $game
      map: $map
      platform: $platform
      wingman: $wingman
      result: $result
      minKills: $minKills
      minMomentKills: $minMomentKills
    ) {
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
        totalCount
      }
      nodes {
        id
        title
        thumbnail
        score
        role
        result
        demoUrl
        playerCount
        placement
        momentCount
        assistCount
        deathCount
        status
        durationInSeconds
        game
        outdated
        gameMode
        killCount
        matchPlayedAt
        knockdowns
        hero
        platform
        matchVod {
          ...MatchVodFields
        }
        ...MatchRankFields
        players {
          accountName
          avatar
          displayName
          gameId
          team
        }
        player {
          avatar
          accountName
          displayName
          gameId
          team
        }
      }
      edges {
        cursor
      }
    }
  }
  ${MATCH_VOD_FIELDS}
  ${MATCH_RANK_FIELDS}
`;

export const MATCH_VOD_DETAIL = gql`
  ${MATCH_VOD_FIELDS}
  query MatchVodDetail($matchId: ID!, $playerGameId: String!) {
    match(id: $matchId, playerGameId: $playerGameId) {
      id
      title
      hero
      result
      matchPlayedAt
      gameMode
      durationInSeconds
      player {
        gameId
      }
      matchVod {
        ...MatchVodFields
      }
    }
  }
`;

export const PROFILE_MATCHES = gql`
  query ProfileMatches(
    $playerGameId: String!
    $after: String
    $first: Int
    $game: Game
    $momentsLimit: Int
  ) {
    matches(
      playerGameId: $playerGameId
      after: $after
      first: $first
      game: $game
    ) {
      nodes {
        id
        title
        thumbnail
        score
        role
        result
        game
        killCount
        deathCount
        matchPlayedAt
        player {
          gameId
        }
        moments(limit: $momentsLimit) {
          id
          title
          score
          timelineLabel
          player {
            avatar
            displayName
            gameId
            team
          }
          displayTags {
            value
            key
          }
          clipTags {
            key
            value
          }
          victimData {
            team
            gameId
            displayName
            avatar
          }
          icons
          victimCount
          clip {
            status
            internalId
            thumbnailUrl(style: STANDARD)
            duration
          }
        }
      }
    }
  }
`;

export const REQUEST_MATCH_MONTAGE = gql`
  mutation RequestMatchMontage(
    $matchId: String!
    $momentIds: [String!]
    $playerGameId: String
  ) {
    requestMatchMontage(
      matchMontageRequestInput: {
        matchId: $matchId
        momentIds: $momentIds
        playerGameId: $playerGameId
      }
    ) {
      errors {
        field
        code
        message
      }
      success
    }
  }
`;

export const REQUEST_FULL_MATCH_VOD = gql`
  mutation RequestFullMatchVod($matchId: String!) {
    requestFullMatchVod(fullMatchVodRequestInput: { matchId: $matchId }) {
      success
      trackId
      errors {
        field
        code
        message
      }
    }
  }
`;

export const MATCH_MONTAGES = gql`
  query UserMatchMontages($demoUrl: String) {
    userMatchMontages(demoUrl: $demoUrl) {
      clip {
        _id
        clipImageThumb
        clipLink
        clipTitle
        createdDate
        id
        status
        trackId
      }
      type
    }
  }
`;

export const REQUEST_SQUAD_MONTAGE = gql`
  mutation RequestSquadMontage(
    $matchId: String!
    $momentIds: [String!]
    $playerGameId: String
    $squadPlayerGameIds: [String!]
  ) {
    requestSquadMontage(
      squadMontageRequestInput: {
        matchId: $matchId
        momentIds: $momentIds
        playerGameId: $playerGameId
        squadPlayerGameIds: $squadPlayerGameIds
      }
    ) {
      errors {
        field
        code
        message
      }
      success
      trackId
    }
  }
`;

const PLAYER_USER_FIELDS = gql`
  fragment PlayerUserFragment on User {
    _id
    username
    avatarUrl
  }
`;

export const GET_MOMENT = gql`
  query GetMoment($momentId: String!) {
    moment(id: $momentId) {
      id
      game
    }
  }
`;

export const PLAYER_MOMENTS = gql`
  query Moments(
    $playerGameId: String!
    $matchId: String
    $sort: MomentSort
    $limit: Int
    $type: MomentType
  ) {
    moments(
      playerGameId: $playerGameId
      matchId: $matchId
      sort: $sort
      limit: $limit
      type: $type
    ) {
      game
      id
      title
      timelineLabel
      match {
        id
      }
      victimData {
        avatar
      }
      icons
      player {
        avatar
        gameId
        displayName
      }
    }
  }
`;

export const MOMENTS = gql`
  ${PLAYER_USER_FIELDS}
  query Moments(
    $playerGameId: String!
    $matchId: String!
    $sort: MomentSort
    $limit: Int
    $onDemand: Boolean
    $type: MomentType
  ) {
    moments(
      playerGameId: $playerGameId
      matchId: $matchId
      sort: $sort
      limit: $limit
      onDemand: $onDemand
      type: $type
    ) {
      id
      title
      timelineLabel
      type
      victimData {
        team
        gameId
        displayName
        avatar
        user {
          ...PlayerUserFragment @nonreactive
        }
      }
      displayTags {
        value
        key
      }
      clipTags {
        key
        value
      }
      icons
      score
      victimCount
      player {
        accountName
        avatar
        displayName
        gameId
        team
        user {
          ...PlayerUserFragment @nonreactive
        }
      }
      game
      clip {
        status
        createdAt
        duration
        id
        previewUrl
        score
        shareId
        thumbnailUrl(style: STANDARD)
        title
        type
        url(orientation: LANDSCAPE)
        user {
          username
        }
        views
      }
      timeline {
        trackId
        overallState
        desktop {
          currentState
          completedAt
        }
        mobile {
          currentState
          completedAt
        }
        clip {
          id
          url(orientation: LANDSCAPE)
          thumbnailUrl(style: STANDARD)
          title
        }
      }
    }
  }
`;

export const MATCH = gql`
  ${PLAYER_USER_FIELDS}
  query Match($matchId: ID!, $playerGameId: String!, $momentsLimit: Int) {
    match(id: $matchId, playerGameId: $playerGameId) {
      hero
      title
      thumbnail
      score
      role
      result
      playerCount
      demoUrl
      outdated
      status
      players {
        accountName
        avatar
        displayName
        gameId
        team
        user {
          ...PlayerUserFragment @nonreactive
        }
      }
      placement
      player {
        avatar
        displayName
        gameId
        team
      }

      moments(limit: $momentsLimit) {
        id
        title
        timelineLabel
        displayTags {
          value
          key
        }
        clipTags {
          key
          value
        }
        victimData {
          team
          gameId
          displayName
          avatar
        }
        icons
        score
        victimCount
        clip {
          createdAt
          duration
          id
          previewUrl
          score
          shareId
          thumbnailUrl(style: STANDARD)
          title
          type
          url(orientation: LANDSCAPE)
          user {
            username
          }
          views
        }
      }
      momentCount
      matchPlayedAt
      killCount
      id
      game
      gameMode
      durationInSeconds
      deathCount
      assistCount
    }
  }
`;

export const MATCH_HISTORY = gql`
  ${MOMENT_CLIP_FIELDS}
  query MatchHistory(
    $page: Int
    $steamId: String
    $excludeMomentsWithClips: Boolean
    $limit: Int
    $excludePreviews: Boolean
    $momentsSort: CSMomentsSort
    $momentsLimit: Int
    $skipSteamApi: Boolean
  ) {
    matchHistory(
      page: $page
      steamId: $steamId
      excludeMomentsWithClips: $excludeMomentsWithClips
      limit: $limit
      excludePreviews: $excludePreviews
      momentsSort: $momentsSort
      momentsLimit: $momentsLimit
      skipSteamApi: $skipSteamApi
    ) {
      player {
        avatar
        name
        steamId
      }
      moments {
        game
        map
        platform
        replayLinkStore
        csgoMatchId
        date
        status
        team
        players {
          avatar
          name
          steamId
        }
        demo {
          platform
          source
        }
        potg {
          round
          hasClip
          clip {
            ...MomentClipFields
          }
        }
        result {
          win
          tie
          score
        }
        kills
        deaths
        matchMontages {
          clip {
            _id
            clipImageThumb
            clipLink
            clipTitle
            createdDate
            id
            status
            trackId
          }
          type
        }
        matchMoments {
          round
          reason
          winner
          kills
          side
          deaths
          weapons
          events
          hasClip
          clipTitle
          players {
            avatar
            name
            steamId
          }
          clip {
            ...MomentClipFields
          }
        }
      }
      matchCount
    }
  }
`;

export const MATCH_HISTORY_LEAGUE_TABLE = gql`
  ${MOMENT_CLIP_FIELDS}
  query MatchHistoryLeague($puuid: String, $page: Int, $limit: Int) {
    leagueMatchHistory(puuid: $puuid, page: $page, limit: $limit) {
      matchCount
      player {
        profileIconId
        puuid
        riotId
      }
      matches {
        computedRole
        date
        win
        matchDuration
        gameMode
        gameType
        champion
        kills
        deaths
        assists
        matchId
        enemyTeam {
          champion
          championId
          puuid
          riotId
        }
        friendlyTeam {
          champion
          championId
          puuid
          riotId
        }
        matchMoments {
          gameClock
          clipTitle
          clipRequested
          clip {
            ...MomentClipFields
          }
          potg
          victims {
            champion
          }
          monsterKills {
            type
          }
        }
        opponent {
          champion
        }
      }
    }
  }
`;
